import { t } from "../../i18n";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatTl, type PlanDto } from "@tasduvar/shared";
import { api } from "../../api/client";

const BASE = (import.meta.env.BASE_URL as string) || "/";
const img = (name: string): string => `${BASE}img/${name}`;

const STEPS: { n: string; title: string; text: string; img: string; alt: string }[] = [
  { n: "1", title: t("Kesiti girin"), text: t("Duvar tipini seçin (taş ağırlık, betonarme konsol, payandalı), ölçüleri çizim üzerinde tıklayıp düzenleyin; zemin, su, yük ve deprem sekmelerini doldurun — ya da zemin etüt raporunu yükleyip değerleri yapay zekâya okutun."), img: "ekran-kesit.jpg", alt: t("Kesit çizimi: ölçüler, kuvvetler ve basınç diyagramı") },
  { n: "2", title: t("Hükümleri anında görün"), text: t("Devrilme, kayma, taşıma gücü, dış merkezlilik; betonarmede kesit tasarımı ve donatı; isteğe bağlı toptan göçme. Her girdi değişiminde sonuçlar ve 3B görünüm yenilenir."), img: "ekran-3b.jpg", alt: t("3B görünüm: duvar, dolgu ve kuvvet okları") },
  { n: "3", title: t("Raporu alın"), text: t("Kurumlara sunulabilir Türkçe/İngilizce rapor: kapak, girdiler, ölçülü kesit, kuvvet şeması, adım adım hesap, donatı planı, metraj ve keşif. PDF, Word, DXF ve XLSX."), img: "ekran-rapor.png", alt: t("Rapor kapağı ve hesap sayfaları") },
];

/** Ana sayfa vitrini: nasıl çalışır (3 adım + ekran görüntüleri) ve planlar (API varsa). */
export function Showcase({ hasApi }: { hasApi: boolean }): JSX.Element {
  const [plans, setPlans] = useState<PlanDto[] | null>(null);
  useEffect(() => { if (hasApi) void api<PlanDto[]>("GET", "/billing/plans").then((p) => setPlans(p.filter((x) => x.priceKurus > 0))).catch(() => setPlans(null)); }, [hasApi]);
  return (
    <>
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12" aria-label={t("Nasıl çalışır")}>
        <h2 className="text-2xl font-semibold text-ink">{t("Nasıl çalışır?")}</h2>
        <ol className="mt-6 grid gap-8 md:grid-cols-3">
          {STEPS.map((s) => (
            <li key={s.n} className="grid gap-3">
              <figure className="rounded-lg border border-rule overflow-hidden bg-sheet shadow-sm">
                <img src={img(s.img)} alt={s.alt} loading="lazy" width={1280} height={800} className="w-full h-auto" />
              </figure>
              <div><div className="text-xs font-mono text-stone">{s.n} / 3</div><h3 className="text-base font-semibold text-ink">{s.title}</h3><p className="mt-1 text-sm leading-relaxed text-ink/75">{s.text}</p></div>
            </li>
          ))}
        </ol>
        <figure className="mt-8 rounded-lg border border-rule overflow-hidden bg-sheet shadow-sm">
          <img src={img("ekran-betonarme.jpg")} alt={t("Payandalı betonarme duvar: donatı şeması ve payanda izi")} loading="lazy" width={1280} height={800} className="w-full h-auto" />
          <figcaption className="px-4 py-2 text-xs text-stone">{t("Payandalı betonarme duvarda donatı şeması, plan görünümü ve payanda kesit tasarımı (TS 500 / EC2 / ACI 318).")}</figcaption>
        </figure>
      </section>

      {hasApi ? (
        <section className="border-t border-rule bg-sheet" aria-label={t("Planlar ve fiyatlar")}>
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-start">
            <div>
              <h2 className="text-2xl font-semibold text-ink">{t("14 gün ücretsiz deneyin")}</h2>
              <p className="mt-3 text-sm leading-relaxed text-ink/75">{t("Hesap açınca tüm özellikler 14 gün açık (raporda deneme filigranı). Sonrasında aylık ya da yıllık abonelik; süre dolunca projeleriniz silinmez, salt okunur kalır ve JSON olarak indirilebilir. Havale/EFT ya da kartla ödeme.")}</p>
              <div className="mt-4 flex flex-wrap gap-2"><Link to="/kayit" className="btn-primary">{t("Hesap oluştur")}</Link><Link to="/planlar" className="btn-ghost border border-rule">{t("Planlar")}</Link><Link to="/kilavuz" className="btn-ghost border border-rule">{t("Kullanım kılavuzu")}</Link></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {(plans ?? []).map((p) => (
                <div key={p.code} className="card p-4">
                  <div className="text-sm font-semibold text-ink">{p.name}</div>
                  <div className="mt-1 text-2xl font-semibold text-accent num">{formatTl(p.priceKurus)} <span className="text-xs text-stone font-normal">/ {p.days === 365 ? t("yıl") : t("ay")}</span></div>
                  <div className="text-xs text-stone mt-1">{t("KDV dâhil")}{p.days === 365 ? ` · ${t("2 ay bedava")}` : ""}</div>
                </div>
              ))}
              {plans && plans.length === 0 ? <p className="text-sm text-stone">{t("Fiyatlar yakında.")}</p> : null}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
